import { useUser } from "@clerk/clerk-react";
import { useMemo } from "react";
import type {
  SubscriptionTier,
  UserSubscription,
  DailyUsage,
  UserMembershipData,
} from "../types/subscription";
import { SUBSCRIPTION_PLANS } from "../types/subscription";

// Clerk metadata 的 key
const MEMBERSHIP_KEY = "membership";

// 获取今天的日期字符串
function getTodayDate(): string {
  return new Date().toISOString().split("T")[0];
}

// 获取默认的免费订阅
function getDefaultSubscription(): UserSubscription {
  return {
    tier: "free",
    startDate: new Date().toISOString(),
  };
}

// 获取默认的每日使用量
function getDefaultDailyUsage(): DailyUsage {
  return {
    date: getTodayDate(),
    deepseekCount: 0,
    gpt4oCount: 0,
    imageGenerationCount: 0,
  };
}

// 获取默认的会员数据
function getDefaultMembershipData(): UserMembershipData {
  return {
    subscription: getDefaultSubscription(),
    dailyUsage: getDefaultDailyUsage(),
  };
}

export function useMembership() {
  const { user, isLoaded } = useUser();

  // 从 Clerk metadata 读取会员数据
  const membershipData = useMemo<UserMembershipData>(() => {
    if (!user || !isLoaded) {
      return getDefaultMembershipData();
    }

    // 从 unsafeMetadata 读取
    const rawData = user.unsafeMetadata?.[MEMBERSHIP_KEY];
    if (!rawData) {
      return getDefaultMembershipData();
    }

    try {
      const data = rawData as UserMembershipData;
      
      // 检查每日使用量是否需要重置（新的一天）
      if (data.dailyUsage.date !== getTodayDate()) {
        data.dailyUsage = getDefaultDailyUsage();
      }

      // 检查订阅是否过期
      if (data.subscription.endDate) {
        const endDate = new Date(data.subscription.endDate);
        if (endDate < new Date()) {
          // 订阅已过期，降级到免费版
          data.subscription = getDefaultSubscription();
        }
      }

      return data;
    } catch (error) {
      console.error("Failed to parse membership data:", error);
      return getDefaultMembershipData();
    }
  }, [user, isLoaded]);

  // 当前套餐信息
  const currentPlan = useMemo(() => {
    return SUBSCRIPTION_PLANS[membershipData.subscription.tier];
  }, [membershipData]);

  // 检查是否可以使用某个模型
  function canUseModel(modelType: "deepseek" | "gpt4o" | "imageGeneration"): boolean {
    const limit = currentPlan.limits[modelType];
    if (limit === "unlimited") return true;
    
    const usage = membershipData.dailyUsage;
    const count =
      modelType === "deepseek"
        ? usage.deepseekCount
        : modelType === "gpt4o"
        ? usage.gpt4oCount
        : usage.imageGenerationCount;

    return count < limit;
  }

  // 获取剩余次数
  function getRemainingUsage(
    modelType: "deepseek" | "gpt4o" | "imageGeneration"
  ): number | "unlimited" {
    const limit = currentPlan.limits[modelType];
    if (limit === "unlimited") return "unlimited";

    const usage = membershipData.dailyUsage;
    const count =
      modelType === "deepseek"
        ? usage.deepseekCount
        : modelType === "gpt4o"
        ? usage.gpt4oCount
        : usage.imageGenerationCount;

    return Math.max(0, limit - count);
  }

  // 记录使用量
  async function recordUsage(
    modelType: "deepseek" | "gpt4o" | "imageGeneration"
  ): Promise<void> {
    if (!user) return;

    const newData = { ...membershipData };
    
    // 增加对应的使用计数
    if (modelType === "deepseek") {
      newData.dailyUsage.deepseekCount++;
    } else if (modelType === "gpt4o") {
      newData.dailyUsage.gpt4oCount++;
    } else {
      newData.dailyUsage.imageGenerationCount++;
    }

    // 更新到 Clerk metadata
    await user.update({
      unsafeMetadata: {
        ...user.unsafeMetadata,
        [MEMBERSHIP_KEY]: newData,
      },
    });
  }

  // 升级套餐
  async function upgradeToTier(tier: SubscriptionTier): Promise<void> {
    if (!user) return;

    const newData: UserMembershipData = {
      ...membershipData,
      subscription: {
        tier,
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 天后到期
      },
    };

    await user.update({
      unsafeMetadata: {
        ...user.unsafeMetadata,
        [MEMBERSHIP_KEY]: newData,
      },
    });
  }

  // 取消订阅
  async function cancelSubscription(): Promise<void> {
    if (!user) return;

    const newData: UserMembershipData = {
      ...membershipData,
      subscription: getDefaultSubscription(),
    };

    await user.update({
      unsafeMetadata: {
        ...user.unsafeMetadata,
        [MEMBERSHIP_KEY]: newData,
      },
    });
  }

  return {
    isLoaded,
    membershipData,
    currentPlan,
    canUseModel,
    getRemainingUsage,
    recordUsage,
    upgradeToTier,
    cancelSubscription,
  };
}
